import { useMemo, useState } from 'react';
import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Route as appRoute } from '../../app.js';
import { useAuthStore } from '../../../lib/auth.js';
import { canSeeOversightQuotes } from '../../../lib/capabilities.js';
import { useOversightQuotes, type OversightQuoteRow } from '../../../hooks/useOversight.js';
import { formatQuoteDate, visaSubclassDisplay, QUOTE_IMMUTABLE_NOTE } from '../../../lib/quotes.js';
import { centsToAudDisplay } from '../../../lib/money.js';
import { editorLabel } from '../../../lib/editor-label.js';
import { Input } from '../../../components/ui/Input.js';
import { Select } from '../../../components/ui/Select.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/oversight/quotes',
  // view_all_branches — owner/accounts (tenant-scoped) AND super_admin, who reads
  // it CROSS-AGENCY (Brief QUOTES-OVERSIGHT). The server's requireCapability +
  // role branch stays the real gate; this guard admits a tenant holder OR a
  // super_admin (who has no tenant of their own but is served every agency).
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    const served = !!user?.tenantId || user?.role === 'super_admin';
    if (!canSeeOversightQuotes(user) || !served) throw redirect({ to: '/app' });
  },
  component: QuotesOverviewRoute,
});

/**
 * The navigation seam. UNEXPORTED and never rendered by a test: useNavigate()
 * closes over a router undefined outside a RouterProvider. The page below takes a
 * plain callback, so it is renderable bare — the quotes-list precedent.
 *
 * Opens the READ-ONLY oversight detail (/app/oversight/quotes/$quoteId), NOT the
 * consultant /app/quotes/$quoteId — that route is create_quotes-gated and would
 * redirect a super_admin (no tenant) or an owner/accounts reader.
 */
function QuotesOverviewRoute() {
  const navigate = useNavigate();
  return (
    <QuotesOverviewPage
      onOpen={(quoteId) => navigate({ to: '/app/oversight/quotes/$quoteId', params: { quoteId } })}
    />
  );
}

/** Live search over the loaded page — client name, email or quote number. */
function matchesQuoteQuery(q: OversightQuoteRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return (
    q.clientName.toLowerCase().includes(needle) ||
    q.clientEmail.toLowerCase().includes(needle) ||
    q.quoteNumber.toLowerCase().includes(needle)
  );
}

/**
 * Cross-branch quotes overview for owner/accounts (Brief OA): every quote the
 * agency has issued, across all seven offices, READ-ONLY. No New quote button, no
 * edit affordance. NO commission column anywhere — the payload carries none and
 * this screen renders none. Money is the SERVER's frozen net; the browser never
 * recomputes it.
 */
export function QuotesOverviewPage({ onOpen }: { onOpen: (quoteId: string) => void }) {
  const { data, isLoading, error, refetch } = useOversightQuotes();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const isSuper = useAuthStore((s) => s.user?.role) === 'super_admin';
  const [search, setSearch] = useState('');
  // Which agency to show. '' = all. Only ever set by super_admin — the tenant
  // payload carries a single agency and hides the control entirely.
  const [agency, setAgency] = useState('');

  const items = useMemo(() => data?.items ?? [], [data]);
  // The distinct agencies present, for the super_admin filter — the same way the
  // audit log lets a super_admin narrow to one agency.
  const agencies = useMemo(
    () =>
      [...new Set(items.map((q) => q.agencyName).filter((n): n is string => !!n))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [items],
  );
  const filtered = useMemo(
    () =>
      items.filter(
        (q) => matchesQuoteQuery(q, search) && (agency === '' || q.agencyName === agency),
      ),
    [items, search, agency],
  );

  return (
    <div className="max-w-5xl">
      <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted font-semibold">Oversight</div>
      <h1 className="font-serif text-4xl tracking-tighter2 mb-2">Quotes overview</h1>
      <p className="text-muted text-sm mb-6">
        {isSuper
          ? 'Every quote issued across every agency, read-only. '
          : 'Every quote your agency has issued, across all offices. '}
        {QUOTE_IMMUTABLE_NOTE}
      </p>

      <div className="flex gap-3">
        <Input
          placeholder="Search client or quote number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {isSuper && (
          <Select
            value={agency}
            onChange={(e) => setAgency(e.target.value)}
            aria-label="Filter by agency"
            className="max-w-xs"
          >
            <option value="">All agencies</option>
            {agencies.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
        )}
      </div>

      <div className="mt-4">
        {isLoading && <p className="px-1 py-6 text-sm text-muted">Loading quotes…</p>}
        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
            Couldn&apos;t load quotes: {(error as Error).message}{' '}
            <button className="underline" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}
        {!isLoading && !error && items.length === 0 && (
          <p className="px-1 py-6 text-sm text-muted">No quotes yet.</p>
        )}
        {!isLoading && !error && items.length > 0 && filtered.length === 0 && (
          <p className="px-1 py-6 text-sm text-muted">No quotes match.</p>
        )}
        {!isLoading && !error && filtered.length > 0 && (
          <div className="surface p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream/60 border-b border-line">
                <tr className="text-left text-muted text-xs uppercase tracking-wider">
                  <th className="px-5 py-3 font-medium">Quote</th>
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium">Client</th>
                  {isSuper && <th className="px-5 py-3 font-medium">Agency</th>}
                  <th className="px-5 py-3 font-medium">Visa</th>
                  <th className="px-5 py-3 font-medium">Branch</th>
                  <th className="px-5 py-3 font-medium text-right">Courses</th>
                  <th className="px-5 py-3 font-medium text-right">Total (net)</th>
                  <th className="px-5 py-3 font-medium">Issued by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map((q) => (
                  <tr key={q.id} className="hover:bg-cream/30 transition-colors align-top">
                    <td className="px-5 py-3 whitespace-nowrap">
                      {/* A button, never a <Link>: Link dereferences the router at
                          render, undefined outside a RouterProvider. */}
                      <button
                        className="text-sm font-medium text-navy underline"
                        onClick={() => onOpen(q.id)}
                      >
                        {q.quoteNumber}
                      </button>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-ink">
                      {formatQuoteDate(q.quoteDate)}
                    </td>
                    <td className="px-5 py-3 min-w-[12rem]">
                      <span className="block text-sm text-ink">{q.clientName}</span>
                      <span className="block text-xs text-muted">{q.clientEmail}</span>
                    </td>
                    {isSuper && (
                      <td className="px-5 py-3 whitespace-nowrap text-sm text-ink">
                        {q.agencyName ?? <span className="text-muted">—</span>}
                      </td>
                    )}
                    <td className="px-5 py-3 whitespace-nowrap text-xs text-muted">
                      {visaSubclassDisplay(q.clientVisaSubclass)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-ink">
                      {/* Derived from the author's current office; null when the
                          author has no branch or was deleted (shown honestly). */}
                      {q.branchName ?? <span className="text-muted">—</span>}
                    </td>
                    <td className="px-5 py-3 text-right text-sm text-ink">{q.lineCount}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap text-sm font-medium text-navy">
                      {/* The SERVER's total — the list carries no lines[] to sum. */}
                      {centsToAudDisplay(q.totalNetFeeCents)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-xs text-muted">
                      {editorLabel(q, currentUserId)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
